import { useState, type FormEvent } from 'react';
import { useI18n } from '@cdn/i18n';
import { useDraft } from '@cdn/identity';
import { Badge, Button, Field, ProblemNotice, TextArea, Unavailable } from '@cdn/ui';
import { useAdmission } from './context';

const form = 'admission.request';

/**
 * Shown under the header of every management view while the person is not admitted to CDN management and preparation, as
 * the family's "not available here" (decision D06):
 * where the person stands (never asked, waiting, declined or revoked), that reads stay open, and the way to
 * ask for admission with an optional note. Admitted people see nothing. Asking again never undoes a decision
 * of the Beyond team, so a declined or revoked person is told so instead of being offered the request.
 */
export function AdmissionNotice() {
	const { t, term } = useI18n();
	const admission = useAdmission();
	const [open, setOpen] = useState(false);
	const document = admission.document.value;
	// Until admission answers, the notice and every action it gates are unknown: an empty wait in the notice's
	// place holds the document's opening for its budget, so a first arrival shows them drawn once (D185)
	if (!admission.settled) return <div className="bui-waiting" style={{ position: 'absolute', width: 0, height: 0, padding: 0 }} aria-hidden="true" data-admission-wait />;
	if (!document || !admission.awaiting) return null;

	// Admission belongs to the organization (decision E3): its standing decides, and only an owner or an
	// administrator asks; anyone else is told whom to ask instead of being offered a request that changes nothing
	const state = admission.organization.value?.state ?? 'none';
	const pending = state === 'waiting' || state === 'invited';
	const title = state === 'revoked' ? term('admission.revoked') : pending ? term('admission.requested') : term('admission.awaiting');
	const text = !admission.asks ? t('admission.notice.ask') : state === 'none' ? term('admission.awaiting.help') : pending ? t('admission.notice.pending') : state === 'declined' ? t('admission.notice.declined') : t('admission.notice.revoked');
	const offered = admission.asks && (state === 'none' || pending);

	return (
		<div data-admission>
			<Unavailable
				kind="access"
				title={title}
				reason={
					<>
						{text}
						{state !== 'none' && (
							<span className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
								<Badge tone={pending ? 'info' : 'warning'}>{term(`waitlist.state.${state}`)}</Badge>
							</span>
						)}
					</>
				}
				owner={t('admission.owner')}
				action={
					offered &&
					(open ? (
						<Request pending={pending} note={admission.organization.value?.entry?.note ?? ''} done={() => setOpen(false)} />
					) : (
						<Button variant={pending ? 'secondary' : 'primary'} onClick={() => setOpen(true)}>
							{pending ? t('admission.form.update') : term('admission.request')}
						</Button>
					))
				}
			/>
		</div>
	);
}

/**
 * The request with its optional note, kept as a draft until the service holds it
 */
function Request({ pending, note, done }: { pending: boolean; note: string; done: () => void }) {
	const { t, term } = useI18n();
	const admission = useAdmission();
	const [draft, setDraft, clear] = useDraft<string>(form, note);
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<unknown>();

	const submit = async (event: FormEvent) => {
		event.preventDefault();
		setBusy(true);
		setProblem(undefined);
		try {
			await admission.seek(draft.trim() || undefined);
			clear();
			done();
		} catch (thrown) {
			setProblem(thrown);
		} finally {
			setBusy(false);
		}
	};

	return (
		<form onSubmit={event => void submit(event)} className="bui-form-width w-full space-y-3">
			{problem !== undefined && <ProblemNotice problem={problem} />}
			<Field label={t('admission.form.note')} hint={t('admission.form.hint')}>
				{control => <TextArea {...control} maxLength={1000} value={draft} onChange={event => setDraft(event.target.value)} />}
			</Field>
			<div className="flex flex-wrap gap-2">
				<Button type="submit" variant="primary" busy={busy}>
					{pending ? t('admission.form.update') : term('admission.request')}
				</Button>
				<Button onClick={done}>{t('ui.cancel')}</Button>
			</div>
		</form>
	);
}
